import { Fragment, useEffect } from 'react'

import classNames from 'classnames'
import { useIntercom } from 'react-use-intercom'

import { useQuery } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCrown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Menu, Transition } from '@headlessui/react'
import { Trans } from '@lingui/macro'
import Link from 'Root/Component/Link'
import { getPricingPageURL } from 'lib/get-pricing-page-url'
import getQueryString from 'lib/get-query-string'
import mixpanelTrack from 'lib/mixpanel-track'
import { useCurrentUserId } from 'lib/use-current-user-id'
import { useIsClient } from 'lib/use-is-client'
import { useRouter } from 'next/router'

import QUERY from './app-header-user-menu.graphql'
import styles from './index.module.css'

library.add(faCrown)

const UserMenu = () => {
  const { update } = useIntercom()
  const userId = useCurrentUserId()
  const isServer = !useIsClient()
  const router = useRouter()
  const { data, loading } = useQuery(QUERY, {
    fetchPolicy: 'cache-first',
    skip: isServer || !Boolean(userId),
  })

  useEffect(() => {
    if (!isServer && Boolean(data?.me?.id)) {
      // @ts-ignore
      if (window.Intercom) {
        update({
          email: data.me.email,
          name: data.me.firstname,
          userId: data.me.id,
        })
      }

      mixpanelTrack('set_user_data', {
        email: data.me.email,
        $email: data.me.email, // https://docs.mixpanel.com/docs/tracking/how-tos/user-profiles#reserved-user-properties
        name: data.me.name,
        vanity_name: data.me.vanity_name,
        piano: data.me.subscriptionPlan.code,
      })
    }
  }, [isServer, data])

  if (isServer || !userId) return null

  return (
    <Menu as="div" className="ml-4 relative flex-shrink-0">
      <div>
        <Menu.Button className={styles.menuButton}>
          <span className="sr-only">
            <Trans>Apri il menu utente</Trans>
          </span>
          <div className={styles.avatar}>
            {!loading && (
              <img
                alt={data?.me?.name}
                className={styles.avatar}
                src={data?.me?.picture}
              />
            )}
          </div>
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className={styles.menuItems}>
          <Menu.Item>
            {() => (
              <div className="block px-4 py-3 border-b mb-1 text-sm">
                <p className="font-medium text-gray-900 truncate">
                  {data?.me?.name || '...'}
                </p>
              </div>
            )}
          </Menu.Item>
          {data?.me?.subscriptionPlan.code === 'essential' && (
            <Menu.Item>
              {({ active }) => (
                <Link
                  className={classNames(styles.menuItem, {
                    'bg-gray-100': active,
                  })}
                  to={getPricingPageURL(getQueryString(router.query.locale))}
                  onClick={() =>
                    mixpanelTrack('Click Passa a Professional menu utente')
                  }
                  style={{ color: 'rgba(148, 137, 250, 1)' }}
                >
                  <FontAwesomeIcon
                    className="text-brand-main mr-1"
                    icon={['fas', 'crown']}
                  />
                  <Trans>Prova Taylora Professional</Trans>
                </Link>
              )}
            </Menu.Item>
          )}
          <Menu.Item>
            {({ active }) => (
              <Link
                className={classNames(styles.menuItem, {
                  'bg-gray-100': active,
                })}
                to={`/profile/${data?.me?.vanity_name}`}
              >
                <Trans>Pagina Profilo</Trans>
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <Link
                className={classNames(styles.menuItem, {
                  'bg-gray-100': active,
                })}
                to="/billing/myplan"
              >
                <Trans>Il mio piano</Trans>
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <Link
                className={classNames(styles.menuItem, {
                  'bg-gray-100': active,
                })}
                to="/my_certificates"
              >
                <Trans>Certificati</Trans>
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <Link
                className={classNames(styles.menuItem, {
                  'bg-gray-100': active,
                })}
                to="/settings"
              >
                <Trans>Impostazioni</Trans>
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <a
                className={classNames(styles.menuItem, {
                  'bg-gray-100': active,
                })}
                href="https://intercom.help/taylora-com/en/"
                target="_blank"
              >
                <Trans>Assistenza</Trans>
              </a>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <a
                className={classNames(styles.menuItem, 'cursor-pointer', {
                  'bg-gray-100': active,
                })}
                onClick={(e) => {
                  e.preventDefault()

                  mixpanelTrack('logout')

                  router.push('/logout')
                }}
              >
                <Trans>Esci</Trans>
              </a>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default UserMenu
